/* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
import React, { useState } from "react";
import Modal from "@visiblethread/react-components/dist/components/Modal";
import Button, {
  buttonVariants,
} from "@visiblethread/react-components/dist/components/Button";
import { hideModal, showModal } from "slices/app/actions/modals";
import { checkedTypes, modalTypes } from "constants/types";
import { useDispatch, useSelector } from "react-redux";
import { exportOrigin, folderBy } from "layout/dictionarySearch/constants";
import Checkbox, {
  checkboxStates,
} from "@visiblethread/react-components/dist/components/Checkbox";
import { getImage, isPdf, isWord } from "utils";
import PropTypes from "prop-types";
import { getDocIdItemsArrayList } from "layout/dictionarySearch/content/ContentPanel";

const ExportMultipleDocumentsModal = ({ checkedItems, origin }) => {
  const dispatch = useDispatch();
  const by = useSelector((state) => state.dictionarySearch.config.folderBy);
  const documents = useSelector((state) => {
    if (by === folderBy.folder) {
      return state.dictionarySearch.folder.documents;
    }
    return state.dictionarySearch.documents;
  });
  const [selectedDocIds, setSelectedDocIds] = useState(
    documents
      .filter(
        (doc) =>
          doc.checkState === checkedTypes.CHECKED ||
          doc.checkState === checkedTypes.INDETERMINATE
      )
      .map((doc) => doc.id)
  );
  const [selectedRow, setSelectedRow] = useState(-1);
  const [hoveredRow, setHoveredRow] = useState(-1);

  const handleCancel = () => {
    dispatch(hideModal(modalTypes.EXPORT_MULTIPLE_DOCS));
  };

  const extensions = () => {
    let pdfSelected = false;
    let wordSelected = false;
    selectedDocIds.forEach((dId) => {
      if (isPdf(documents.find((d) => d.id === dId)?.name)) {
        pdfSelected = true;
      }
      if (isWord(documents.find((d) => d.id === dId)?.name)) {
        wordSelected = true;
      }
    });
    return { pdfSelected, wordSelected };
  };

  const handleSave = () => {
    dispatch(hideModal(modalTypes.EXPORT_MULTIPLE_DOCS));

    const selectedDocs = documents.filter((doc) =>
      selectedDocIds.includes(doc.id)
    );
    dispatch(
      showModal(modalTypes.EXPORT_CONTENT, {
        documentsWithTerms: getDocIdItemsArrayList(
          by,
          checkedItems,
          selectedDocs
        ),
        origin,
        extensions: extensions(),
      })
    );
  };

  const handleSelectAll = () => {
    if (selectedDocIds.length === documents.length) {
      setSelectedDocIds([]);
    } else {
      setSelectedDocIds(documents.map((d) => d.id));
    }
  };

  const handleSelect = (id, index) => {
    if (selectedDocIds.includes(id)) {
      setSelectedDocIds(selectedDocIds.filter((d) => d !== id));
    } else {
      setSelectedDocIds([...selectedDocIds, id]);
    }
    setSelectedRow(index);
  };

  const getCheckboxState = () => {
    if (selectedDocIds.length === documents.length)
      return checkboxStates.checked;
    if (selectedDocIds.length > 0) return checkboxStates.indeterminate;
    return checkboxStates.empty;
  };

  const modalBody = () => (
    <div
      className="export_multiple_docs"
      data-testid="export-multiple-docs-modal"
    >
      <span>
        Check the documents you wish to include in the Shred and click
        &lsquo;Next&lsquo; below.
      </span>
      <div className="export_multiple_docs__select_all">
        <span onClick={handleSelectAll}>Select All</span>
        <Checkbox state={getCheckboxState()} handleChange={handleSelectAll} />
      </div>
      <div className="export_multiple_docs__table">
        {documents.map((d, index) => (
          <div
            onMouseEnter={() => setHoveredRow(index)}
            onMouseLeave={() => setHoveredRow(-1)}
          >
            {(hoveredRow === index || selectedRow === index) && (
              <div
                className="highlight"
                onClick={() => handleSelect(d.id, index)}
              />
            )}
            <span>
              <img src={getImage(d.name)} alt="doc-img" />
              {d.name}
            </span>
            <Checkbox
              state={
                selectedDocIds.includes(d.id)
                  ? checkboxStates.checked
                  : checkboxStates.empty
              }
              handleChange={() => handleSelect(d.id, index)}
            />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <Modal
      data-test="exportMultipleDocsModal"
      className="fade-in export_multi_docs"
      show
      onDismiss={handleCancel}
      backdrop="static"
      centered
    >
      <Modal.Header closeButton>
        <b>Export Multiple Documents</b>
      </Modal.Header>
      <Modal.Body>{modalBody()}</Modal.Body>
      <Modal.Footer>
        <Button
          onClick={handleSave}
          variant={buttonVariants.primary}
          dataTestId="nextButton"
          disabled={selectedDocIds.length === 0}
          content="Next"
        />
        <Button
          onClick={handleCancel}
          variant={buttonVariants.secondary}
          dataTestId="cancelButton"
          disabled={false}
          content="Cancel"
        />
      </Modal.Footer>
    </Modal>
  );
};

export default ExportMultipleDocumentsModal;

ExportMultipleDocumentsModal.propTypes = {
  checkedItems: PropTypes.arrayOf(PropTypes.string),
  origin: PropTypes.oneOf([
    exportOrigin.documents,
    exportOrigin.simpleShred,
    exportOrigin.catShred,
  ]),
};
